import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EmailDetail, EmailTestStatus } from "@regigov/contracts";
import { ApiError } from "../api/client";
import { emailTestApi } from "../api/email";
import {
  DefinitionList,
  Field,
  ProblemAlert,
  StatusBadge,
  formatDateTime,
} from "./ui";

const TTL_OPTIONS = [
  { seconds: 5 * 60, label: "5 minutes" },
  { seconds: 15 * 60, label: "15 minutes" },
  { seconds: 60 * 60, label: "1 hour" },
  { seconds: 24 * 60 * 60, label: "24 hours" },
];

/** Still waiting on the worker: queued, or failed with a retry scheduled. */
const pending = (e: EmailDetail): boolean =>
  e.status === "queued" || (e.status === "error" && e.nextAttemptAt !== null);

/** CER admin page (EMAIL_TEST_UI=true) for sending a test email through the queue and watching what the worker does with it. */
export function EmailTestPage() {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ["email-test", "status"],
    queryFn: emailTestApi.status,
    refetchInterval: 10_000,
  });
  const emails = useQuery({
    queryKey: ["email-test", "emails"],
    queryFn: emailTestApi.emails,
    refetchInterval: 5_000,
    enabled: status.isSuccess,
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState({
    to: "",
    subject: "RegiGov test email",
    body: "This is a test email from the RegiGov email test page.",
    ttlSeconds: 15 * 60,
  });

  const send = useMutation({
    mutationFn: emailTestApi.send,
    onSuccess: (email) => {
      queryClient.setQueryData(["email-test", "email", email.id], email);
      setSelectedId(email.id);
      void queryClient.invalidateQueries({
        queryKey: ["email-test", "emails"],
      });
    },
  });

  if (
    status.error instanceof ApiError &&
    (status.error.status === 404 || status.error.status === 403)
  ) {
    return (
      <>
        <h1>Email test</h1>
        <p className="muted">
          {status.error.status === 404
            ? "The email test page is turned off. Set EMAIL_TEST_UI=true on the API to use it."
            : "Only CER administrators can use the email test page."}
        </p>
      </>
    );
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    send.mutate(form);
  };

  return (
    <>
      <h1>Email test</h1>
      <p className="muted">
        Queues a test email the same way application notifications are queued.
        The worker sends it through the configured transport, retries a failed
        attempt after the retry delay and marks it expired if it is still unsent
        when its time to live runs out. See <code>docs/email.md</code>.
      </p>
      <ProblemAlert error={status.error ?? emails.error} />
      {status.data && <SenderStatus status={status.data} />}

      <h2>Send a test email</h2>
      <form className="form" onSubmit={onSubmit}>
        <Field label="To">
          <input
            type="email"
            required
            value={form.to}
            onChange={(e) => setForm({ ...form, to: e.target.value })}
          />
        </Field>
        <Field label="Subject">
          <input
            required
            maxLength={200}
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
          />
        </Field>
        <Field label="Message">
          <textarea
            required
            maxLength={5000}
            rows={4}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
          />
        </Field>
        <Field
          label="Time to live"
          hint="If the email hasn't been sent by then it is marked expired and never sent."
        >
          <select
            value={form.ttlSeconds}
            onChange={(e) =>
              setForm({ ...form, ttlSeconds: Number(e.target.value) })
            }
          >
            {TTL_OPTIONS.map((o) => (
              <option key={o.seconds} value={o.seconds}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <ProblemAlert error={send.error} />
        <div className="row">
          <button type="submit" disabled={send.isPending}>
            {send.isPending ? "Queuing…" : "Queue email"}
          </button>
        </div>
      </form>

      {selectedId !== null && <EmailPanel id={selectedId} />}

      <h2>Recent emails</h2>
      {emails.data && emails.data.items.length === 0 && (
        <p className="muted">No emails queued yet.</p>
      )}
      {emails.data && emails.data.items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Kind</th>
              <th>To</th>
              <th>Subject</th>
              <th>Status</th>
              <th>Attempts</th>
              <th>Queued</th>
              <th>Last error</th>
            </tr>
          </thead>
          <tbody>
            {emails.data.items.map((e) => (
              <tr key={e.id}>
                <td>
                  <button
                    className="linklike"
                    onClick={() => setSelectedId(e.id)}
                  >
                    {e.id}
                  </button>
                </td>
                <td>
                  <code>{e.kind}</code>
                </td>
                <td>{e.recipient}</td>
                <td>{e.subject}</td>
                <td>
                  <StatusBadge status={e.status} />
                </td>
                <td>{e.attempts}</td>
                <td>{formatDateTime(e.createdAt)}</td>
                <td>
                  {e.lastError ? (
                    <code>{e.lastError.code}</code>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function SenderStatus({ status }: { status: EmailTestStatus }) {
  const w = status.worker;
  return (
    <>
      <h2>Sender</h2>
      {!w && (
        <div role="alert" className="alert">
          No worker has reported yet. Queued emails won't be sent until the
          worker is running.
        </div>
      )}
      {w && !w.online && (
        <div role="alert" className="alert">
          The worker last reported at {formatDateTime(w.seenAt)}. Queued emails
          won't be sent until it is running again.
        </div>
      )}
      {w?.transport === "none" && (
        <div role="alert" className="alert">
          EMAIL_TRANSPORT is none: the worker sends nothing and queued emails
          expire unsent.
        </div>
      )}
      {w && (
        <DefinitionList
          items={[
            ["Transport", <code key="t">{w.transport}</code>],
            ["Sender address", w.senderAddress],
            ["ACS endpoint", w.endpointHost],
            [
              "Retries",
              `${status.defaultMaxRetries} resends, ${Math.round(w.retryDelayMs / 60_000)} minutes apart`,
            ],
            ["Worker started", formatDateTime(w.startedAt)],
            ["Worker last seen", formatDateTime(w.seenAt)],
          ]}
        />
      )}
    </>
  );
}

function EmailPanel({ id }: { id: number }) {
  const email = useQuery({
    queryKey: ["email-test", "email", id],
    queryFn: () => emailTestApi.email(id),
    refetchInterval: (query) =>
      query.state.data && !pending(query.state.data) ? false : 2_000,
  });
  const e = email.data;
  return (
    <section className="card" aria-label={`Email ${id}`}>
      <h2>Email {id}</h2>
      <ProblemAlert error={email.error} />
      {e && (
        <>
          <DefinitionList
            items={[
              ["Status", <StatusBadge key="s" status={e.status} />],
              ["To", e.recipient],
              ["Subject", e.subject],
              ["Attempts", `${e.attempts} of ${e.maxRetries + 1}`],
              [
                "Next attempt",
                e.nextAttemptAt ? formatDateTime(e.nextAttemptAt) : null,
              ],
              ["Expires", formatDateTime(e.expiresAt)],
              ["Sent", e.sentAt ? formatDateTime(e.sentAt) : null],
              [
                "Provider message ID",
                e.providerMessageId ? (
                  <code key="m">{e.providerMessageId}</code>
                ) : null,
              ],
            ]}
          />
          {pending(e) && <p className="muted">Waiting for the worker…</p>}
          {e.status === "error" && e.nextAttemptAt === null && (
            <div role="alert" className="alert">
              Delivery failed and every retry has been used.
            </div>
          )}
          {e.status === "expired" && (
            <div role="alert" className="alert">
              The email expired before it could be sent.
            </div>
          )}
          {e.errors.length > 0 && (
            <>
              <h3>Delivery errors</h3>
              <table className="table">
                <thead>
                  <tr>
                    <th>Attempt</th>
                    <th>Code</th>
                    <th>HTTP status</th>
                    <th>Message</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {e.errors.map((err) => (
                    <tr key={err.attempt}>
                      <td>{err.attempt}</td>
                      <td>
                        <code>{err.code}</code>
                      </td>
                      <td>{err.statusCode ?? "—"}</td>
                      <td>{err.message}</td>
                      <td>{formatDateTime(err.occurredAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          <h3>Message</h3>
          {e.textBody === null ? (
            <p className="muted">
              The body is cleared once an email is sent, expired or out of
              retries, because it can hold live links.
            </p>
          ) : (
            <pre className="email-preview">{e.textBody}</pre>
          )}
        </>
      )}
    </section>
  );
}
